import React, { useState, useCallback, useRef, useEffect } from 'react';
import '../../styles/SelectionBox.css';

interface SelectionBoxProps {
  /** 选择完成回调 */
  onSelectionChange: (postIds: string[], isAdditive: boolean) => void;
  /** 是否启用框选 */
  enabled?: boolean;
  /** 帖子卡片选择器，用于动态查找帖子元素 */
  postSelector?: string;
  children: React.ReactNode;
}

interface SelectionRect {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export const SelectionBox: React.FC<SelectionBoxProps> = ({
  onSelectionChange,
  enabled = true,
  postSelector = '[data-post-id]',
  children,
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const boxContainerRef = useRef<HTMLDivElement>(null);

  // 计算两个矩形是否相交
  const rectsIntersect = (rect1: DOMRect, rect2: { left: number; top: number; right: number; bottom: number }) => {
    return !(
      rect1.right < rect2.left ||
      rect1.left > rect2.right ||
      rect1.bottom < rect2.top ||
      rect1.top > rect2.bottom
    );
  };

  // 获取选择框内的帖子（动态查询 DOM）
  const getSelectedPostIds = useCallback((rect: SelectionRect): string[] => {
    const container = boxContainerRef.current;
    if (!container) return [];

    const containerRect = container.getBoundingClientRect();
    const selectionBox = {
      left: Math.min(rect.startX, rect.endX) + containerRect.left - container.scrollLeft,
      top: Math.min(rect.startY, rect.endY) + containerRect.top - container.scrollTop,
      right: Math.max(rect.startX, rect.endX) + containerRect.left - container.scrollLeft,
      bottom: Math.max(rect.startY, rect.endY) + containerRect.top - container.scrollTop,
    };

    // 动态查询所有帖子元素
    const postElements = container.querySelectorAll(postSelector);
    const selectedIds: string[] = [];

    postElements.forEach((element) => {
      const postId = element.getAttribute('data-post-id');
      if (!postId) return;

      const elementRect = element.getBoundingClientRect();
      if (rectsIntersect(elementRect, selectionBox)) {
        selectedIds.push(postId);
      }
    });

    return selectedIds;
  }, [postSelector]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // 只响应左键
    if (e.button !== 0 || !enabled) return;

    // 只排除真正的交互元素（按钮、链接、输入框、模态框）
    // 不排除 post-card 本身，以便支持框选
    const target = e.target as HTMLElement;
    if (target.closest('button') ||
        target.closest('a') ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('.ant-modal') ||
        target.closest('.ant-dropdown')) return;

    const container = boxContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left + container.scrollLeft;
    const y = e.clientY - rect.top + container.scrollTop;

    startPoint.current = { x, y };
    setIsSelecting(true);
    setSelectionRect({ startX: x, startY: y, endX: x, endY: y });

    // 阻止默认的文本选择行为
    e.preventDefault();
    e.stopPropagation();
  }, [enabled]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isSelecting || !startPoint.current) return;

    const container = boxContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left + container.scrollLeft;
    const y = e.clientY - rect.top + container.scrollTop;

    setSelectionRect({
      startX: startPoint.current.x,
      startY: startPoint.current.y,
      endX: x,
      endY: y,
    });

    e.preventDefault();
  }, [isSelecting]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (!isSelecting || !selectionRect) {
      setIsSelecting(false);
      setSelectionRect(null);
      return;
    }

    // 只有当选框有一定大小时才触发选择
    const width = Math.abs(selectionRect.endX - selectionRect.startX);
    const height = Math.abs(selectionRect.endY - selectionRect.startY);

    if (width > 5 || height > 5) {
      const selectedIds = getSelectedPostIds(selectionRect);
      const isAdditive = e.shiftKey; // Shift 键追加选择
      onSelectionChange(selectedIds, isAdditive);
    }

    setIsSelecting(false);
    setSelectionRect(null);
    startPoint.current = null;
  }, [isSelecting, selectionRect, getSelectedPostIds, onSelectionChange]);

  // 全局鼠标事件处理（防止鼠标移出容器后丢失事件）
  useEffect(() => {
    if (!isSelecting) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!startPoint.current) return;

      const container = boxContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left + container.scrollLeft;
      const y = e.clientY - rect.top + container.scrollTop;

      setSelectionRect({
        startX: startPoint.current.x,
        startY: startPoint.current.y,
        endX: x,
        endY: y,
      });
    };

    const handleGlobalMouseUp = (e: MouseEvent) => {
      if (!selectionRect) {
        setIsSelecting(false);
        setSelectionRect(null);
        return;
      }

      const width = Math.abs(selectionRect.endX - selectionRect.startX);
      const height = Math.abs(selectionRect.endY - selectionRect.startY);

      if (width > 5 || height > 5) {
        const selectedIds = getSelectedPostIds(selectionRect);
        const isAdditive = e.shiftKey;
        onSelectionChange(selectedIds, isAdditive);
      }

      setIsSelecting(false);
      setSelectionRect(null);
      startPoint.current = null;
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isSelecting, selectionRect, getSelectedPostIds, onSelectionChange]);

  // 计算选择框的样式
  const getSelectionStyle = (): React.CSSProperties | undefined => {
    if (!selectionRect) return undefined;

    const left = Math.min(selectionRect.startX, selectionRect.endX);
    const top = Math.min(selectionRect.startY, selectionRect.endY);
    const width = Math.abs(selectionRect.endX - selectionRect.startX);
    const height = Math.abs(selectionRect.endY - selectionRect.startY);

    return {
      left,
      top,
      width,
      height,
    };
  };

  // 阻止浏览器默认选择和拖拽行为
  useEffect(() => {
    const container = boxContainerRef.current;
    if (!container) return;

    const handleSelectStart = (e: Event) => {
      // 框选进行中时阻止默认选择
      if (isSelecting) {
        e.preventDefault();
      }
    };

    const handleDragStart = (e: Event) => {
      e.preventDefault();
    };

    container.addEventListener('selectstart', handleSelectStart);
    container.addEventListener('dragstart', handleDragStart);

    return () => {
      container.removeEventListener('selectstart', handleSelectStart);
      container.removeEventListener('dragstart', handleDragStart);
    };
  }, [isSelecting]);

  return (
    <div
      ref={boxContainerRef}
      className={`selection-box-container ${isSelecting ? 'is-selecting' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {children}
      {isSelecting && selectionRect && (
        <div className="selection-box-rect" style={getSelectionStyle()} />
      )}
    </div>
  );
};
